/**
 * MultiCurrencyPage — currency management.
 * Table: Code | Name | Symbol | Rate vs USD | Last refreshed | Active toggle
 * Form:  Add Currency (ISO code autocomplete, manual rate override, auto-refresh toggle)
 * Action: Refresh all rates
 *
 * Ported from legacy apps/dash-legacy/src/routes/_authed/settings/currency.tsx.
 * Modernized: plain Tailwind only, no shadcn/ui, no sonner, no lucide-react.
 */
import { useMemo, useState, type FormEvent } from "react";
import {
	useMultiCurrencyCreate,
	useMultiCurrencyDelete,
	useMultiCurrencyList,
	useMultiCurrencyRefreshAll,
	useMultiCurrencyUpdate,
} from "./hooks";
import type { CurrencyRow } from "./api";

const CARD = "rounded-lg border border-zinc-200 bg-white p-5 shadow-sm";
const INPUT =
	"w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500/20 disabled:cursor-not-allowed disabled:bg-zinc-50";
const BTN_PRIMARY =
	"rounded-md border border-sky-600 bg-gradient-to-r from-emerald-500 to-sky-500 px-3 py-1.5 text-sm font-medium text-white hover:from-emerald-600 hover:to-sky-600 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_SECONDARY =
	"rounded-md border border-zinc-300 bg-white px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"rounded-md border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";

const ISO_CATALOG: readonly {
	code: string;
	name: string;
	symbol: string;
}[] = [
	{ code: "USD", name: "US Dollar", symbol: "$" },
	{ code: "EUR", name: "Euro", symbol: "\u20AC" },
	{ code: "GBP", name: "British Pound", symbol: "\u00A3" },
	{ code: "CAD", name: "Canadian Dollar", symbol: "C$" },
	{ code: "AUD", name: "Australian Dollar", symbol: "A$" },
	{ code: "JPY", name: "Japanese Yen", symbol: "\u00A5" },
	{ code: "CHF", name: "Swiss Franc", symbol: "CHF" },
	{ code: "INR", name: "Indian Rupee", symbol: "\u20B9" },
	{ code: "BRL", name: "Brazilian Real", symbol: "R$" },
	{ code: "CNY", name: "Chinese Yuan", symbol: "\u00A5" },
	{ code: "MXN", name: "Mexican Peso", symbol: "Mex$" },
	{ code: "SEK", name: "Swedish Krona", symbol: "kr" },
	{ code: "NOK", name: "Norwegian Krone", symbol: "kr" },
	{ code: "NZD", name: "New Zealand Dollar", symbol: "NZ$" },
	{ code: "SGD", name: "Singapore Dollar", symbol: "S$" },
	{ code: "HKD", name: "Hong Kong Dollar", symbol: "HK$" },
];

function fmtDate(s: string | null): string {
	if (!s) return "Never";
	const d = new Date(s);
	if (Number.isNaN(d.getTime())) return "Never";
	return d.toLocaleString("en-US", {
		month: "short",
		day: "numeric",
		hour: "numeric",
		minute: "2-digit",
	});
}

function fmtRate(n: number): string {
	if (!Number.isFinite(n)) return "-";
	return n.toFixed(6);
}

interface AutocompleteProps {
	value: string;
	onChange: (code: string) => void;
	onPick: (iso: (typeof ISO_CATALOG)[number]) => void;
	disabled?: boolean;
	excludeCodes: ReadonlySet<string>;
}

function IsoAutocomplete({
	value,
	onChange,
	onPick,
	disabled,
	excludeCodes,
}: AutocompleteProps) {
	const [open, setOpen] = useState(false);
	const q = value.trim().toUpperCase();
	const suggestions = useMemo(() => {
		const filtered = ISO_CATALOG.filter(
			(c) => !excludeCodes.has(c.code.toUpperCase()),
		);
		if (!q) return filtered.slice(0, 8);
		return filtered
			.filter(
				(c) =>
					c.code.toUpperCase().includes(q) ||
					c.name.toUpperCase().includes(q),
			)
			.slice(0, 8);
	}, [q, excludeCodes]);

	return (
		<div className="relative">
			<input
				type="text"
				className={INPUT}
				placeholder="USD"
				value={value}
				onChange={(e) => {
					onChange(e.currentTarget.value.toUpperCase());
					setOpen(true);
				}}
				onFocus={() => setOpen(true)}
				onBlur={() => {
					// Delay so click on suggestion registers.
					setTimeout(() => setOpen(false), 120);
				}}
				disabled={disabled}
				maxLength={8}
				autoComplete="off"
				aria-label="ISO currency code"
				data-testid="multi-currency-code-input"
			/>
			{open && suggestions.length > 0 ? (
				<ul
					className="absolute z-20 mt-1 max-h-56 w-full overflow-auto rounded-md border border-zinc-200 bg-white shadow-lg"
					data-testid="multi-currency-code-suggestions"
				>
					{suggestions.map((s) => (
						<li key={s.code}>
							<button
								type="button"
								className="flex w-full items-center justify-between px-3 py-1.5 text-left text-sm hover:bg-zinc-50"
								onMouseDown={(e) => {
									e.preventDefault();
									onPick(s);
									setOpen(false);
								}}
							>
								<span className="font-mono text-xs font-semibold text-zinc-900">
									{s.code}
								</span>
								<span className="truncate px-2 text-xs text-zinc-500">
									{s.name}
								</span>
								<span className="text-xs text-zinc-400">{s.symbol}</span>
							</button>
						</li>
					))}
				</ul>
			) : null}
		</div>
	);
}

interface ToggleProps {
	checked: boolean;
	onChange: (v: boolean) => void;
	disabled?: boolean;
	ariaLabel: string;
	testId?: string;
}

function Toggle({ checked, onChange, disabled, ariaLabel, testId }: ToggleProps) {
	return (
		<button
			type="button"
			role="switch"
			aria-checked={checked}
			aria-label={ariaLabel}
			data-testid={testId}
			disabled={disabled}
			onClick={() => onChange(!checked)}
			className={[
				"relative inline-flex h-5 w-9 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors",
				checked ? "bg-emerald-500" : "bg-zinc-300",
				disabled ? "cursor-not-allowed opacity-60" : "",
			].join(" ")}
		>
			<span
				className={[
					"pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transition-transform",
					checked ? "translate-x-4" : "translate-x-0",
				].join(" ")}
			/>
		</button>
	);
}

interface RowActionProps {
	row: CurrencyRow;
	onToggleActive: (row: CurrencyRow) => void;
	onToggleAuto: (row: CurrencyRow) => void;
	onDelete: (row: CurrencyRow) => void;
	busy: boolean;
}

function CurrencyTableRow({
	row,
	onToggleActive,
	onToggleAuto,
	onDelete,
	busy,
}: RowActionProps) {
	return (
		<tr
			className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50/50"
			data-testid={`multi-currency-row-${row.id}`}
		>
			<td className="px-4 py-2.5 font-mono text-xs font-semibold text-zinc-900">
				{row.code}
			</td>
			<td className="px-4 py-2.5 text-sm text-zinc-700">{row.name}</td>
			<td className="px-4 py-2.5 text-sm text-zinc-700">{row.symbol}</td>
			<td className="px-4 py-2.5 text-right font-mono text-xs tabular-nums text-zinc-700">
				{fmtRate(row.rate_vs_usd)}
			</td>
			<td className="px-4 py-2.5 text-xs text-zinc-500">
				{fmtDate(row.last_refreshed_at)}
			</td>
			<td className="px-4 py-2.5">
				<Toggle
					checked={row.is_active}
					onChange={() => onToggleActive(row)}
					disabled={busy}
					ariaLabel={`Toggle active for ${row.code}`}
					testId={`multi-currency-active-${row.id}`}
				/>
			</td>
			<td className="px-4 py-2.5">
				<Toggle
					checked={row.auto_refresh}
					onChange={() => onToggleAuto(row)}
					disabled={busy}
					ariaLabel={`Toggle auto-refresh for ${row.code}`}
					testId={`multi-currency-auto-${row.id}`}
				/>
			</td>
			<td className="px-4 py-2.5 text-right">
				<button
					type="button"
					className={BTN_GHOST}
					onClick={() => onDelete(row)}
					disabled={busy}
					data-testid={`multi-currency-delete-${row.id}`}
				>
					Remove
				</button>
			</td>
		</tr>
	);
}

export function MultiCurrencyPage() {
	const listQuery = useMultiCurrencyList();
	const createMut = useMultiCurrencyCreate();
	const updateMut = useMultiCurrencyUpdate();
	const deleteMut = useMultiCurrencyDelete();
	const refreshMut = useMultiCurrencyRefreshAll();

	const [code, setCode] = useState("");
	const [name, setName] = useState("");
	const [symbol, setSymbol] = useState("");
	const [rate, setRate] = useState("");
	const [autoRefresh, setAutoRefresh] = useState(true);

	const items = listQuery.data?.items ?? [];
	const existingCodes = useMemo(
		() => new Set(items.map((i) => i.code.toUpperCase())),
		[items],
	);

	const lastRefreshedAt = useMemo(() => {
		let best: string | null = null;
		for (const it of items) {
			if (!it.last_refreshed_at) continue;
			if (!best || it.last_refreshed_at > best) best = it.last_refreshed_at;
		}
		return best;
	}, [items]);

	const pickIso = (iso: (typeof ISO_CATALOG)[number]) => {
		setCode(iso.code);
		if (!name.trim()) setName(iso.name);
		if (!symbol.trim()) setSymbol(iso.symbol);
	};

	const resetForm = () => {
		setCode("");
		setName("");
		setSymbol("");
		setRate("");
		setAutoRefresh(true);
	};

	const handleCreate = (e: FormEvent) => {
		e.preventDefault();
		const codeTrim = code.trim().toUpperCase();
		if (!codeTrim) return;
		const body = {
			code: codeTrim,
			name: name.trim() || undefined,
			symbol: symbol.trim() || undefined,
			rate_vs_usd: rate.trim() ? Number(rate) : undefined,
			auto_refresh: autoRefresh,
		};
		createMut.mutate(body, {
			onSuccess: resetForm,
		});
	};

	const handleToggleActive = (row: CurrencyRow) => {
		updateMut.mutate({ id: row.id, patch: { is_active: !row.is_active } });
	};

	const handleToggleAuto = (row: CurrencyRow) => {
		updateMut.mutate({
			id: row.id,
			patch: { auto_refresh: !row.auto_refresh },
		});
	};

	const handleDelete = (row: CurrencyRow) => {
		deleteMut.mutate(row.id);
	};

	const rowBusy = updateMut.isPending || deleteMut.isPending;

	return (
		<main
			data-testid="multi-currency-page"
			className="mx-auto flex w-full max-w-6xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
					multi-currency
				</p>
				<div className="flex flex-wrap items-end justify-between gap-3">
					<h1 className="bg-gradient-to-r from-emerald-600 to-sky-600 bg-clip-text text-3xl font-semibold text-transparent">
						Multi Currency
					</h1>
					<div className="flex items-center gap-3">
						<span className="text-xs text-zinc-500">
							Last refreshed:{" "}
							<span className="font-medium text-zinc-700">
								{fmtDate(lastRefreshedAt)}
							</span>
						</span>
						<button
							type="button"
							className={BTN_SECONDARY}
							onClick={() => refreshMut.mutate()}
							disabled={refreshMut.isPending}
							data-testid="multi-currency-refresh-all"
						>
							{refreshMut.isPending ? "Refreshing." : "Refresh all rates"}
						</button>
					</div>
				</div>
				<p className="text-sm text-zinc-500">
					Enabled currencies, exchange rates, and auto-refresh configuration.
				</p>
			</header>

			<section className={CARD} aria-label="Add currency">
				<h2 className="text-base font-semibold text-zinc-900">Add currency</h2>
				<form
					onSubmit={handleCreate}
					className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4"
					data-testid="multi-currency-create-form"
				>
					<div className="flex flex-col gap-1.5 lg:col-span-1">
						<label className="text-xs font-medium text-zinc-700">
							ISO code
						</label>
						<IsoAutocomplete
							value={code}
							onChange={setCode}
							onPick={pickIso}
							disabled={createMut.isPending}
							excludeCodes={existingCodes}
						/>
					</div>
					<div className="flex flex-col gap-1.5 lg:col-span-1">
						<label className="text-xs font-medium text-zinc-700">Name</label>
						<input
							type="text"
							className={INPUT}
							placeholder="US Dollar"
							value={name}
							onChange={(e) => setName(e.currentTarget.value)}
							disabled={createMut.isPending}
							data-testid="multi-currency-name-input"
						/>
					</div>
					<div className="flex flex-col gap-1.5 lg:col-span-1">
						<label className="text-xs font-medium text-zinc-700">Symbol</label>
						<input
							type="text"
							className={INPUT}
							placeholder="$"
							value={symbol}
							onChange={(e) => setSymbol(e.currentTarget.value)}
							disabled={createMut.isPending}
							maxLength={6}
							data-testid="multi-currency-symbol-input"
						/>
					</div>
					<div className="flex flex-col gap-1.5 lg:col-span-1">
						<label className="text-xs font-medium text-zinc-700">
							Rate override vs USD
						</label>
						<input
							type="number"
							inputMode="decimal"
							min={0}
							step="0.0001"
							className={INPUT}
							placeholder="1.0000"
							value={rate}
							onChange={(e) => setRate(e.currentTarget.value)}
							disabled={createMut.isPending}
							data-testid="multi-currency-rate-input"
						/>
					</div>
					<div className="flex items-center gap-3 lg:col-span-2">
						<Toggle
							checked={autoRefresh}
							onChange={setAutoRefresh}
							disabled={createMut.isPending}
							ariaLabel="Auto refresh for new currency"
							testId="multi-currency-auto-refresh-input"
						/>
						<div>
							<span className="text-sm font-medium text-zinc-700">
								Auto-refresh rate
							</span>
							<p className="text-xs text-zinc-500">
								Fetch this currency's rate on each refresh run.
							</p>
						</div>
					</div>
					<div className="flex items-end justify-end lg:col-span-2">
						<button
							type="submit"
							className={BTN_PRIMARY}
							disabled={createMut.isPending || !code.trim()}
							data-testid="multi-currency-submit"
						>
							{createMut.isPending ? "Adding." : "Add currency"}
						</button>
					</div>
				</form>
				{createMut.error ? (
					<p className="mt-2 text-xs text-red-600" role="alert">
						{createMut.error.message}
					</p>
				) : null}
			</section>

			<section className={CARD} aria-label="Currency list">
				<div className="flex items-center justify-between">
					<h2 className="text-base font-semibold text-zinc-900">Currencies</h2>
					<span className="text-xs text-zinc-500">
						{items.length} currenc{items.length === 1 ? "y" : "ies"}
					</span>
				</div>
				{listQuery.isLoading ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="multi-currency-loading"
					>
						Loading.
					</p>
				) : listQuery.error ? (
					<p className="mt-3 text-sm text-red-600" role="alert">
						{listQuery.error.message}
					</p>
				) : items.length === 0 ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="multi-currency-empty"
					>
						No currencies enabled yet. Add one above to get started.
					</p>
				) : (
					<div
						className="mt-3 overflow-hidden rounded-md border border-zinc-200"
						data-testid="multi-currency-list"
					>
						<table className="min-w-full text-sm">
							<thead>
								<tr className="border-b border-zinc-200 bg-zinc-50 text-left text-xs font-medium uppercase tracking-wide text-zinc-500">
									<th className="px-4 py-2.5">Code</th>
									<th className="px-4 py-2.5">Name</th>
									<th className="px-4 py-2.5">Symbol</th>
									<th className="px-4 py-2.5 text-right">Rate vs USD</th>
									<th className="px-4 py-2.5">Last refreshed</th>
									<th className="px-4 py-2.5">Active</th>
									<th className="px-4 py-2.5">Auto-refresh</th>
									<th className="px-4 py-2.5 text-right">Actions</th>
								</tr>
							</thead>
							<tbody>
								{items.map((row) => (
									<CurrencyTableRow
										key={row.id}
										row={row}
										onToggleActive={handleToggleActive}
										onToggleAuto={handleToggleAuto}
										onDelete={handleDelete}
										busy={rowBusy}
									/>
								))}
							</tbody>
						</table>
					</div>
				)}
				{refreshMut.error ? (
					<p className="mt-2 text-xs text-red-600" role="alert">
						{refreshMut.error.message}
					</p>
				) : null}
				{updateMut.error ? (
					<p className="mt-2 text-xs text-red-600" role="alert">
						{updateMut.error.message}
					</p>
				) : null}
				{deleteMut.error ? (
					<p className="mt-2 text-xs text-red-600" role="alert">
						{deleteMut.error.message}
					</p>
				) : null}
			</section>
		</main>
	);
}

export default MultiCurrencyPage;
